import {
  BedDoubleLight,
  HotelSingleBedAddLight,
  House3Light,
  MultipleActionsAddLight,
  MultipleCircleLight,
  RealEstateDimensionsBlockLight,
  ResizeExpandSidesLight,
  TaskListCheckLight,
} from '@ha/icons';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { TranslateFunc } from '@ha/intl';
import { useIntl } from 'ha/i18n';
import { FacilitiesValues } from 'ha/models/ListingFacilities/constants';
import { isListingFurnished } from 'ha/models/ListingFacilities/isListingFurnished';
import {
  ListingKindTranslations,
  ListingTypeTranslations,
  EntirePlaceTypeTranslations,
} from 'ha/models/Listing/translations';
import { Grid, Typography, units } from '@hbf/dsl';
import {
  ListingCancellationPolicy,
  ListingFacilities,
} from '@ha/api/v2/types/Listing';
import { getAreaLabel } from 'ha/helpers/unitConverter';

const useStyles = makeStyles()({
  highlightItem: {
    paddingBottom: units.rem(1),
  },
  icon: {
    marginRight: '20px',
  },
});

export interface Props {
  kind: 0 | 1 | 2;
  type: 1 | 2 | 3;
  facilities: ListingFacilities;
  freePlaces: number;
  currentOccupancy: number;
  cancellationPolicy?: ListingCancellationPolicy;
  isImperialSystem: boolean;
}

const getHousematesText = (
  T: TranslateFunc,
  currentOccupancy: number,
  gender: string | null,
) => {
  const housemates = currentOccupancy === 1 ? T('housemate') : T('housemates');

  switch (gender) {
    case FacilitiesValues.PreferredMale:
    case FacilitiesValues.SharedWithBoys:
      return `${currentOccupancy} ${housemates} ${T('(male)')}`;
    case FacilitiesValues.PreferredFemale:
    case FacilitiesValues.SharedWithGirls:
      return `${currentOccupancy} ${housemates} ${T('(female)')}`;
    default:
      return `${currentOccupancy} ${housemates} ${T('(mixed)')}`;
  }
};

const formattedBedroomCount = (
  T: TranslateFunc,
  facilities: ListingFacilities,
) => {
  switch (facilities.bedroom_count) {
    case '0':
      return T('Studio');
    case '1':
      return `1 ${T('bedroom')}`;
    default:
      return `${facilities.bedroom_count} ${T('bedrooms')}`;
  }
};

export const ListingHighlights: React.FC<Props> = ({
  kind,
  type,
  facilities,
  freePlaces,
  currentOccupancy,
  cancellationPolicy,
  isImperialSystem,
}) => {
  const { T } = useIntl();
  const { classes } = useStyles();

  const isEntirePlace = kind === 0;
  const isFurnished = isListingFurnished({ facilities });
  const formattedOccupancy =
    currentOccupancy > 0
      ? getHousematesText(T, currentOccupancy, facilities.housemates_gender)
      : T('no housemates');

  const bedroomSize = useMemo(() => {
    if (isEntirePlace) {
      return '';
    }
    return getAreaLabel(facilities.bedroom_size, isImperialSystem);
  }, [isEntirePlace, facilities.bedroom_size, isImperialSystem]);

  const propertySize = useMemo(
    () => getAreaLabel(facilities.total_size, isImperialSystem),
    [facilities.total_size, isImperialSystem],
  );

  return (
    <Grid container data-test-locator="Listing/ListingHighlights">
      <Grid
        container
        item
        className={classes.highlightItem}
        xs={12}
        sm={4}
        alignItems="center"
      >
        <House3Light
          className={classes.icon}
          data-test-locator="Listing/ListingHighlights/Kind/Icon"
        />
        <Typography.Paragraph
          display="inline"
          data-test-locator="Listing/ListingHighlights/Kind"
        >
          {isEntirePlace
            ? T(EntirePlaceTypeTranslations[type])
            : T(
                '%s in %s',
                T(ListingKindTranslations[kind]),
                (T(ListingTypeTranslations[type]) || '').toLowerCase(),
              )}
        </Typography.Paragraph>
      </Grid>
      {!isEntirePlace && facilities.bedroom_size && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/Bedroom"
        >
          <ResizeExpandSidesLight
            className={classes.icon}
            data-test-locator="Listing/ListingHighlights/Bedroom/Icon"
          />
          <Typography.Paragraph display="inline">
            {T('Bedroom')}
            {`: ${bedroomSize}`}
          </Typography.Paragraph>
        </Grid>
      )}
      {facilities.total_size && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/Property"
        >
          <RealEstateDimensionsBlockLight
            className={classes.icon}
            data-test-locator="Listing/ListingHighlights/Property/Icon"
          />
          <Typography.Paragraph display="inline">
            {T('Property')}
            {`: ${propertySize}`}
          </Typography.Paragraph>
        </Grid>
      )}
      {isFurnished && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/Furnished"
        >
          <BedDoubleLight
            className={classes.icon}
            data-test-locator="Listing/ListingHighlights/Furnished/Icon"
          />
          <Typography.Paragraph display="inline">
            {T('Furnished')}
          </Typography.Paragraph>
        </Grid>
      )}

      <Grid
        container
        item
        className={classes.highlightItem}
        xs={12}
        sm={4}
        alignItems="center"
        data-test-locator="Listing/ListingHighlights/Space"
      >
        <MultipleCircleLight
          className={classes.icon}
          data-test-locator="Listing/ListingHighlights/Space/Icon"
        />
        <Typography.Paragraph display="inline">
          {freePlaces === 1
            ? T('Space for 1 person')
            : T('Space for %s persons', freePlaces)}
        </Typography.Paragraph>
      </Grid>
      {isEntirePlace && facilities.bedroom_count && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/Bedroom Count"
        >
          <HotelSingleBedAddLight
            className={classes.icon}
            data-test-locator="Listing/ListingHighlights/BedroomCount/Icon"
          />
          <Typography.Paragraph display="inline">
            {formattedBedroomCount(T, facilities)}
          </Typography.Paragraph>
        </Grid>
      )}
      {!isEntirePlace && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/Current Occupancy"
        >
          <MultipleActionsAddLight
            className={classes.icon}
            data-test-locator="Listing/ListingHighlights/CurrentOccupancy/Icon"
          />
          <Typography.Paragraph display="inline">
            {formattedOccupancy}
          </Typography.Paragraph>
        </Grid>
      )}
      {cancellationPolicy === ListingCancellationPolicy.FLEXIBLE_30D && (
        <Grid
          container
          item
          className={classes.highlightItem}
          xs={12}
          sm={4}
          alignItems="center"
          data-test-locator="Listing/ListingHighlights/CancellationPolicy"
        >
          <TaskListCheckLight className={classes.icon} />
          <Typography.Paragraph display="inline">
            {T('listings.details.cancellation_policy.highlight')}
          </Typography.Paragraph>
        </Grid>
      )}
    </Grid>
  );
};
